import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/Sidebar";
import api from "../../api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { exportToExcel } from "../../utils/exportExcel";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import {
  ArrowLeft, Download, Clock, CalendarDays, CheckSquare,
  TrendingUp, Coffee, Trophy
} from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
};

const fmtExport = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
};

const getBreakMins = (breaks = []) =>
  breaks.filter((b) => b.start && b.end)
    .reduce((acc, b) => acc + (new Date(b.end) - new Date(b.start)) / 60000, 0);

const statusColors = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};
const taskStatusColors = {
  todo: "bg-slate-100 text-slate-600",
  in_progress: "bg-sky-100 text-sky-700",
  done: "bg-emerald-100 text-emerald-700",
};

const StatCard = ({ icon: Icon, label, value, sub, color, bg }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex items-start gap-3 stat-card-hover">
    <div className={`p-2.5 rounded-xl ${bg} shrink-0`}><Icon size={18} className={color} /></div>
    <div>
      <p className="text-slate-500 text-xs font-medium">{label}</p>
      <p className="text-2xl font-bold text-[#1a2332]">{value}</p>
      {sub && <p className="text-slate-400 text-xs mt-0.5">{sub}</p>}
    </div>
  </div>
);

export default function EmployeeReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get(`/reports/employee/${id}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load report"))
      .finally(() => setLoading(false));
  }, [id]);

  const handleExportFull = async () => {
    setExporting(true);
    try {
      const emp = data.employee;
      const attSheet = data.attendance.map((r) => ({
        Date: r.date,
        "Clock In": fmtExport(r.clock_in),
        "Clock Out": fmtExport(r.clock_out),
        "Break (mins)": Math.round(getBreakMins(r.breaks)),
        "Active Hours": r.active_hours || 0,
        Status: r.clock_out ? "Completed" : r.clock_in ? "Active" : "Absent",
      }));
      const leavesSheet = data.leaves.map((l) => ({
        "Start Date": l.start_date,
        "End Date": l.end_date,
        "Days": Math.ceil((new Date(l.end_date) - new Date(l.start_date)) / 86400000) + 1,
        Reason: l.reason,
        Status: l.status,
        "Rejection Reason": l.rejection_reason || "",
        Submitted: new Date(l.created_at).toLocaleDateString("en-IN"),
      }));
      const tasksSheet = data.tasks.map((t) => ({
        Title: t.title,
        Description: t.description || "",
        Priority: t.priority,
        "Due Date": t.due_date || "",
        Status: t.status,
        Created: new Date(t.created_at).toLocaleDateString("en-IN"),
      }));
      exportToExcel(
        [
          { data: attSheet, name: "Attendance" },
          { data: leavesSheet, name: "Leaves" },
          { data: tasksSheet, name: "Tasks" },
        ],
        `${emp.first_name}_${emp.last_name}_Report`
      );
      toast.success("Report exported successfully!");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-8">
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[...Array(6)].map((_, i) => <div key={i} className="h-24 bg-white rounded-xl border border-slate-200 animate-pulse" />)}
          </div>
        </div>
      </Layout>
    );
  }

  if (!data) return <Layout><div className="p-8 text-slate-400">Employee not found</div></Layout>;

  const { employee: emp, attendance, leaves, tasks, stats } = data;

  const chartData = attendance.slice().reverse().map((r) => ({
    date: r.date.slice(5),
    "Work Hrs": parseFloat((r.active_hours || 0).toFixed(2)),
    "Break Hrs": parseFloat((getBreakMins(r.breaks) / 60).toFixed(2)),
  }));

  const initials = `${emp.first_name[0]}${emp.last_name[0]}`.toUpperCase();

  return (
    <Layout>
      <div className="p-8 page-enter">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-7">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/admin/employees")}
              className="p-2 rounded-lg hover:bg-slate-200 transition-colors text-slate-600"
              data-testid="back-btn"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-[#38b6e8]/20 flex items-center justify-center">
                <span className="text-[#38b6e8] font-bold text-lg">{initials}</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{emp.first_name} {emp.last_name}</h1>
                <p className="text-slate-500 text-sm">{emp.job_role} · Joined {emp.joining_date}</p>
              </div>
            </div>
          </div>
          <Button
            onClick={handleExportFull}
            disabled={exporting}
            className="bg-[#1a2332] hover:bg-[#2c3e50] text-white gap-2"
            data-testid="export-full-report-btn"
          >
            <Download size={16} />
            {exporting ? "Exporting..." : "Export Full Report"}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-7">
          <StatCard icon={Clock} label="Days Present" value={stats.total_days_present} color="text-[#38b6e8]" bg="bg-sky-50" />
          <StatCard icon={TrendingUp} label="Total Hours" value={`${stats.total_hours_worked}h`} color="text-emerald-600" bg="bg-emerald-50" />
          <StatCard icon={TrendingUp} label="Avg Hours/Day" value={`${stats.average_hours_per_day}h`} sub="daily avg" color="text-violet-600" bg="bg-violet-50" />
          <StatCard icon={Coffee} label="Total Break" value={`${stats.total_break_minutes}m`} sub="total break time" color="text-amber-600" bg="bg-amber-50" />
          <StatCard icon={CalendarDays} label="Leaves Taken" value={stats.leaves_taken} sub="approved" color="text-rose-600" bg="bg-rose-50" />
          <StatCard icon={Trophy} label="Tasks Done" value={stats.tasks_completed} sub={`of ${tasks.length}`} color="text-emerald-700" bg="bg-emerald-50" />
        </div>

        {/* Chart */}
        {chartData.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
            <h3 className="font-semibold text-slate-900 mb-5">Daily Work Hours vs Break Time</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} barSize={16} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} unit="h" />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                  formatter={(v, n) => [`${v}h`, n]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Work Hrs" fill="#38b6e8" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Break Hrs" fill="#f59e0b" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Attendance Table */}
        <SectionTable
          title="Attendance History"
          count={attendance.length}
          onExport={() => {
            const sheet = attendance.map((r) => ({
              Date: r.date, "Clock In": fmtExport(r.clock_in), "Clock Out": fmtExport(r.clock_out),
              "Break (mins)": Math.round(getBreakMins(r.breaks)), "Active Hours": r.active_hours || 0,
              Status: r.clock_out ? "Completed" : r.clock_in ? "Active" : "Absent",
            }));
            exportToExcel([{ data: sheet, name: "Attendance" }], `${emp.first_name}_Attendance`);
          }}
          empty="No attendance records"
          headers={["Date", "Clock In", "Clock Out", "Break Time", "Active Hours", "Status"]}
        >
          {attendance.map((r) => (
            <tr key={r.id || r.date} className="hover:bg-slate-50/60 border-b border-slate-100 last:border-0">
              <td className="px-5 py-3 text-sm font-medium text-slate-700">{r.date}</td>
              <td className="px-5 py-3 text-sm text-emerald-600 font-medium">{fmt(r.clock_in)}</td>
              <td className="px-5 py-3 text-sm text-slate-600">{fmt(r.clock_out)}</td>
              <td className="px-5 py-3">
                <span className="text-sm font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                  {getBreakMins(r.breaks) > 0 ? `${Math.round(getBreakMins(r.breaks))}m` : "—"}
                </span>
              </td>
              <td className="px-5 py-3">
                <span className="font-bold text-[#1a2332] text-sm">{r.active_hours > 0 ? `${r.active_hours}h` : "—"}</span>
              </td>
              <td className="px-5 py-3">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                  r.clock_out ? "bg-emerald-100 text-emerald-700" : r.clock_in ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-500"
                }`}>
                  {r.clock_out ? "Completed" : r.clock_in ? "Active" : "Absent"}
                </span>
              </td>
            </tr>
          ))}
        </SectionTable>

        {/* Leave Table */}
        <SectionTable
          title="Leave History"
          count={leaves.length}
          onExport={() => {
            const sheet = leaves.map((l) => ({
              "Start Date": l.start_date, "End Date": l.end_date,
              Days: Math.ceil((new Date(l.end_date) - new Date(l.start_date)) / 86400000) + 1,
              Reason: l.reason, Status: l.status, "Rejection Reason": l.rejection_reason || "",
            }));
            exportToExcel([{ data: sheet, name: "Leaves" }], `${emp.first_name}_Leaves`);
          }}
          empty="No leave records"
          headers={["Period", "Days", "Reason", "Status", "Submitted"]}
        >
          {leaves.map((l) => (
            <tr key={l.id} className="hover:bg-slate-50/60 border-b border-slate-100 last:border-0">
              <td className="px-5 py-3 text-sm">
                <p className="font-medium text-slate-800">{l.start_date}</p>
                <p className="text-slate-400 text-xs">to {l.end_date}</p>
              </td>
              <td className="px-5 py-3">
                <span className="bg-slate-100 text-slate-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                  {Math.ceil((new Date(l.end_date) - new Date(l.start_date)) / 86400000) + 1}d
                </span>
              </td>
              <td className="px-5 py-3 text-sm text-slate-600 max-w-xs truncate">{l.reason}</td>
              <td className="px-5 py-3">
                <Badge className={`${statusColors[l.status]} border text-xs capitalize`}>{l.status}</Badge>
              </td>
              <td className="px-5 py-3 text-sm text-slate-500">{new Date(l.created_at).toLocaleDateString("en-IN")}</td>
            </tr>
          ))}
        </SectionTable>

        {/* Tasks Table */}
        <SectionTable
          title="Task Summary"
          count={tasks.length}
          onExport={() => {
            const sheet = tasks.map((t) => ({
              Title: t.title, Description: t.description || "",
              Priority: t.priority, "Due Date": t.due_date || "", Status: t.status,
            }));
            exportToExcel([{ data: sheet, name: "Tasks" }], `${emp.first_name}_Tasks`);
          }}
          empty="No tasks assigned"
          headers={["Title", "Priority", "Due Date", "Status"]}
        >
          {tasks.map((t) => (
            <tr key={t.id} className="hover:bg-slate-50/60 border-b border-slate-100 last:border-0">
              <td className="px-5 py-3">
                <p className="font-medium text-slate-800 text-sm">{t.title}</p>
                {t.description && <p className="text-slate-400 text-xs truncate max-w-xs">{t.description}</p>}
              </td>
              <td className="px-5 py-3">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${
                  t.priority === "high" ? "bg-red-100 text-red-600" : t.priority === "medium" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"
                }`}>{t.priority}</span>
              </td>
              <td className="px-5 py-3 text-sm text-slate-500">{t.due_date || "—"}</td>
              <td className="px-5 py-3">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${taskStatusColors[t.status]}`}>
                  {t.status.replace("_", " ")}
                </span>
              </td>
            </tr>
          ))}
        </SectionTable>
      </div>
    </Layout>
  );
}

// Reusable section wrapper
const SectionTable = ({ title, count, onExport, empty, headers, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6">
    <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold text-slate-900">{title}</h3>
        <span className="bg-slate-100 text-slate-600 text-xs font-semibold px-2 py-0.5 rounded-full">{count}</span>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={onExport}
        className="gap-1.5 border-slate-200 text-slate-600 hover:border-[#38b6e8] hover:text-[#38b6e8] text-xs"
        data-testid={`export-${title.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <Download size={13} /> Export
      </Button>
    </div>
    {count === 0 ? (
      <div className="p-10 text-center text-slate-400 text-sm">{empty}</div>
    ) : (
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            {headers.map((h) => (
              <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-3">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    )}
  </div>
);
